import React from 'react';
import { MoreHorizontal, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Category,
  CategoryType,
  formatCategoryType,
  getCategoryTypeColor
} from '../../types/category';
import { useCategoryMutations } from '../../hooks/useCategoryMutations';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';

interface CategoryTableProps {
  categories: Category[];
  selectedCategories: Set<number>;
  onSelectCategory: (categoryId: number, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onEdit: (category: Category) => void;
  onDelete?: () => void; // Optional callback for parent notification (real-time handles the update)
}

export function CategoryTable({
  categories,
  selectedCategories,
  onSelectCategory,
  onSelectAll,
  onEdit,
  onDelete
}: CategoryTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [categoryToDelete, setCategoryToDelete] = React.useState<Category | null>(null);
  const { deleteCategory } = useCategoryMutations();

  const allSelected = categories.length > 0 && categories.every(c => selectedCategories.has(c.id));
  const someSelected = categories.some(c => selectedCategories.has(c.id));

  const handleDelete = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
      await deleteCategory(categoryToDelete.id, categoryToDelete.name);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      // Real-time subscription will auto-update the list
      onDelete?.(); // Optional callback for parent notification
    }
  };

  return (
    <>
      <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                onCheckedChange={onSelectAll}
                indeterminate={someSelected && !allSelected}
                aria-label="Selecionar todas as categorias"
              />
            </TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Criada em</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                Nenhuma categoria encontrada
              </TableCell>
            </TableRow>
          ) : (
            categories.map((category) => (
              <TableRow 
                key={category.id}
                className="group hover:bg-muted/50 transition-colors"
              >
                <TableCell>
                  <Checkbox
                    checked={selectedCategories.has(category.id)}
                    onCheckedChange={(checked) => 
                      onSelectCategory(category.id, checked as boolean)
                    }
                    aria-label={`Selecionar categoria ${category.name}`}
                  />
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback 
                        className="text-xs font-medium"
                        style={{ 
                          backgroundColor: category.color || '#3b82f6',
                          color: 'white'
                        }}
                      >
                        {category.name.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm">{category.name}</div>
                      {category.description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {category.description}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`${getCategoryTypeColor(category.type)} text-xs min-w-[5rem] justify-center`}
                  >
                    {formatCategoryType(category.type)}
                  </Badge>
                </TableCell>

                <TableCell>
                  <div className="text-sm text-muted-foreground">
                    {new Date(category.created_at).toLocaleDateString('pt-BR')}
                  </div>
                </TableCell>
                
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Abrir menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        onClick={() => onEdit(category)}
                        className="gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Editar
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onClick={() => handleDelete(category)}
                        className="gap-2 text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>

    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
          <AlertDialogDescription>
            Tem certeza que deseja excluir a categoria "{categoryToDelete?.name}"?
            Esta ação não pode ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Excluir
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}